<template>
  <v-main>
    <v-container fluid grid-list-md>
      <v-layout row wrap>
        <v-flex sx12>
          <h1 class="mt-n2 mb-4">{{ ct.TitleText }}</h1>
          <v-progress-linear v-if="loading" :indeterminate="true"></v-progress-linear>
          <v-card v-else raised>
            <v-card-text>
              <v-form ref="form" v-model="valid">
                <v-layout justify-end row>
                  <v-checkbox :label="ct.AllowMSAccountLogin" v-model="form.allowMSAccountLogin" :color="'primary'"></v-checkbox>
                  <v-checkbox class="ml-10" :label="ct.Active" v-model="form.active" :color="'primary'"></v-checkbox>
                </v-layout>
                <v-select
                  v-if="isAdmin()"
                  :items="corporations"
                  v-model="form.corporation"
                  :label="ct.Corporation"
                  :rules="[rules.required]"
                  :no-data-text="ct.NoDataAvailable"
                  @change="loadFacilities();loadUserGroups()"
                ></v-select>
                <v-text-field v-model="form.name" :label="ct.Name" :rules="[rules.required]"></v-text-field>
                <v-text-field v-model="form.fullName" :label="ct.Full_name" :rules="[rules.required]"></v-text-field>
                <v-text-field v-model="form.email" :label="ct.Email" :rules="[rules.required, rules.email]"></v-text-field>
                <v-select chips multiple deletable-chips auto-select-first v-model="form.facilities" :label="ct.Facilities" :items="facilities" :no-data-text="ct.NoDataAvailable"></v-select>
                <v-select chips multiple deletable-chips auto-select-first v-model="form.usergroups" :label="ct.UserGroups" :items="usergroups" :no-data-text="ct.NoDataAvailable"></v-select>
                <v-text-field v-model="form.customerNo" :label="$t('CustomerNo')" :placeholder="$t('Comma-separated values are supported')"></v-text-field>
                <v-text-field v-model="form.carrierNo" :label="$t('CarrierNo')"></v-text-field>
                <v-text-field v-model="form.producerNo" :label="$t('ProducerNo')"></v-text-field>
                <v-text-field v-model="form.vehicleLicensePlateNo" :label="$t('VehicleLicensePlateNo')"></v-text-field>

                <v-card class="mt-4" v-if="hasClassifierClaim" raised>
                  <v-card-text>
                    <v-form ref="form">
                      <v-layout justify-end></v-layout>
                      <h3 class="mb-4">{{$t('Classifier')}} : {{$t('Settings')}}</h3>
                        <v-checkbox v-model="form.disableApprovalRights" :label="$t('Hide Accept button (Classifier approval rights)')" :color="'primary'"></v-checkbox>
                        <v-checkbox v-model="form.showGotoDeliveryInspection" :label="$t('Show Go to Delivery Inspection button')" :color="'primary'"></v-checkbox>
                    </v-form>
                  </v-card-text>
                </v-card>

                <v-card class="mt-4" v-if="hasDeliveryClaim || hasScanMobileClaim" raised>
                  <v-card-text>
                    <v-form ref="form">
                      <v-layout justify-end></v-layout>
                      <h3 class="mb-4">{{$t('Scan Mobile')}} : {{$t('Settings')}}</h3>
                        <v-checkbox v-model="form.showGotoDeliveryInspection" :label="$t('Show Go to Delivery Inspection button')" :color="'primary'"></v-checkbox>
                        <v-checkbox v-model="form.internalUserVehicle" :label="$t('Internal user vehicle')" :color="'primary'"></v-checkbox>
                        <v-text-field v-model="form.zplPrinterIp" :label="$t('Local ZPL Printer IP number (x.x.x.101,x.x.x.102)')" :placeholder="$t('Fixed printer IP needed')" :rules="[rules.ip]">
                          <template v-slot:append>
                            <v-btn class="mb-1" color="success" :disabled="!form.zplPrinterIp || form.zplPrinterIp.includes(',')" :href="'https://' + form.zplPrinterIp" target="_blank">{{ 'TEST ' + (!form.zplPrinterIp || form.zplPrinterIp.includes(',') ? 'https' : 'https://' + form.zplPrinterIp )}}</v-btn>
                          </template>
                        </v-text-field>
                        <v-flex justify-end d-flex textsize-small class="v-ip">{{ 'Remember: Advanced | Continue to ' + (form.zplPrinterIp === undefined || !form.zplPrinterIp || form.zplPrinterIp.includes(',') ? 'x.x.x.x' : 'https://' + form.zplPrinterIp ) + ' (unsafe)' }}</v-flex>
                        <v-text-field v-model="form.zplPrinterWidth" :label="$t('Local ZPL Printer Width (alternative)')" :placeholder="$t('Default Width is 576')" :rules="[rules.number]">
                        </v-text-field>
                    </v-form>
                  </v-card-text>
                </v-card>

                <v-card class="mt-4" v-if="hasScanMobileClaim" raised>
                  <v-card-text>
                    <v-form ref="form">
                      <v-layout justify-end></v-layout>
                      <h3 class="mb-4">{{$t('Delivery')}} : {{$t('Settings')}}</h3>
                        <v-checkbox v-model="form.createDeliveriesAllAgreements" :label="$t('Can create delivery for all agreements')" :color="'primary'"></v-checkbox>
                        <v-checkbox v-model="form.createVehiclesAllCarriers" :label="$t('Can create vehicles for all carriers')" :color="'primary'"></v-checkbox>
                    </v-form>
                  </v-card-text>
                </v-card>

                <v-card class="mt-4" v-if="hasWheelLoaderClaim" raised>
                  <v-card-text>
                    <v-form ref="form">
                      <v-layout justify-end></v-layout>
                      <h3 class="mb-4">{{$t('Wheel loader')}} : {{$t('Settings')}}</h3>
                        <v-text-field v-model="form.zplPrinterIpWl" :label="$t('Local ZPL Printer IP number (x.x.x.101,x.x.x.102)')" :placeholder="$t('Fixed printer IP needed')" :rules="[rules.ip]">
                          <template v-slot:append>
                            <v-btn class="mb-1" color="success" :disabled="!form.zplPrinterIpWl || form.zplPrinterIpWl.includes(',')" :href="'https://' + form.zplPrinterIpWl" target="_blank">{{ 'TEST ' + (!form.zplPrinterIpWl || form.zplPrinterIpWl.includes(',') ? 'https' : 'https://' + form.zplPrinterIpWl )}}</v-btn>
                          </template>
                        </v-text-field>
                        <v-flex justify-end d-flex textsize-small class="v-ip">{{ 'Remember: Advanced | Continue to ' + (form.zplPrinterIpWl === undefined || !form.zplPrinterIpWl || form.zplPrinterIpWl.includes(',') ? 'x.x.x.x' : 'https://' + form.zplPrinterIpWl ) + ' (unsafe)' }}</v-flex>
                        <v-text-field v-model="form.zplPrinterWidthWl" :label="$t('Local ZPL Printer Width (alternative)')" :placeholder="$t('Default Width is 576')" :rules="[rules.number]">
                        </v-text-field>
                    </v-form>
                  </v-card-text>
                </v-card>

              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-flex justify-start d-flex textsize-small class="v-ip">{{ ' NOTE: User data is cached. When saving a user via the ScanPortal User Interface the cache is refreshed with any changes in the database.' }}</v-flex>
              <v-spacer></v-spacer>
              <v-btn
                :loading="submitting"
                :disabled="!valid"
                @click="submit"
                color="success" min-width="97px"
              >{{ct.SaveBtnText}} {{buttonAppend}}</v-btn>
              <v-btn color="primary" @click="preventLeave = false; $router.go(-1)">{{$t("Cancel")}}</v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </v-main>
</template>

<script>
import { app } from '@/main'
import * as Admin from '@/core/admin'
import * as Security from '@/core/security'
import ct from '@/core/mixins/common-translations'
import rules from '@/core/mixins/common-rules'
import preventLeave from '@/core/mixins/preventLeave'
import * as License from '@/core/license'

export default {
  mixins: [ct, rules, preventLeave],
  data: () => ({
    dataType: 'user',
    loading: true,
    submitting: false,
    valid: true,
    redirectTo: 'users',
    id: '00000000-0000-0000-0000-000000000000',
    form: {
      name: '',
      fullName: '',
      email: '',
      corporation: 1,
      active: true,
      allowMSAccountLogin: false,
      facilities: [],
      usergroups: [],
      customerNo: '',
      carrierNo: '',
      producerNo: '',
      vehicleLicensePlateNo: '',
      disableApprovalRights: false,
      showGotoDeliveryInspection: false,
      internalUserVehicle: false,
      zplPrinterIp: '',
      zplPrinterWidth: '',
      createDeliveriesAllAgreements: false,
      createVehiclesAllCarriers: false,
      zplPrinterIpWl: '',
      zplPrinterWidthWl: ''
    },
    corporations: [],
    facilities: [],
    usergroups: [],
    details: [],
    hasClassifierClaim: false,
    hasScanMobileClaim: false,
    hasDeliveryClaim: false,
    hasWheelLoaderClaim: false
  }),

  methods: {
    isAdmin () {
      return Security.hasAccess([License.adminNo])
    },

    isAsphaltVehicle () {
      const vehicleUsersId = this.usergroups.find(u => u.text === 'VehicleUsers')?.value ?? '<not found>'
      return this.form.usergroups.includes(vehicleUsersId)
    },

    async submit () {
      this.details = this.details.filter(o => o.Name !== 'CustomerNo' &&
         o.Name !== 'CarrierNo' &&
         o.Name !== 'ProducerNo' &&
         o.Name !== 'VehicleLicensePlateNo' &&
         o.Name !== 'DisableApprovalRights' &&
         o.Name !== 'ShowGotoDeliveryInspection' &&
         o.Name !== 'ZplPrinterIp' &&
         o.Name !== 'ZplPrinterWidth' &&
         o.Name !== 'InternalUserVehicle' &&
         o.Name !== 'CreateDeliveriesAllAgreements' &&
         o.Name !== 'CreateVehiclesAllCarriers' &&
         o.Name !== 'ZplPrinterIpWl' &&
         o.Name !== 'ZplPrinterWidthWl')
      if (this.form.customerNo) { this.form.customerNo = this.form.customerNo.replaceAll(' ', '').replaceAll('\'', '').replaceAll('"', '').replaceAll(',,', '') }
      this.details.push({ Name: 'CustomerNo', Value: this.form.customerNo })
      this.details.push({ Name: 'CarrierNo', Value: this.form.carrierNo })
      this.details.push({ Name: 'ProducerNo', Value: this.form.producerNo })
      this.details.push({ Name: 'VehicleLicensePlateNo', Value: this.form.vehicleLicensePlateNo })
      this.details.push({ Name: 'DisableApprovalRights', Value: this.form.disableApprovalRights.toString() })
      this.details.push({ Name: 'ShowGotoDeliveryInspection', Value: this.form.showGotoDeliveryInspection.toString() })
      this.details.push({ Name: 'ZplPrinterIp', Value: this.form.zplPrinterIp })
      this.details.push({ Name: 'ZplPrinterWidth', Value: this.form.zplPrinterWidth })
      this.details.push({ Name: 'InternalUserVehicle', Value: this.form.internalUserVehicle.toString() })
      this.details.push({ Name: 'CreateDeliveriesAllAgreements', Value: this.form.createDeliveriesAllAgreements.toString() })
      this.details.push({ Name: 'CreateVehiclesAllCarriers', Value: this.form.createVehiclesAllCarriers.toString() })
      this.details.push({ Name: 'ZplPrinterIpWl', Value: this.form.zplPrinterIpWl })
      this.details.push({ Name: 'ZplPrinterWidthWl', Value: this.form.zplPrinterWidthWl })
      this.preventLeave = false
      if (this.$refs.form.validate()) {
        this.submitting = true
        const res = await Admin.saveUser({
          Id: this.id,
          Name: this.form.name,
          FullName: this.form.fullName,
          Email: this.form.email,
          CorporationId: this.form.corporation,
          Active: this.form.active,
          Facilities: this.form.facilities,
          UserGroups: this.form.usergroups,
          Details: this.details,
          allowMSAccountLogin: this.form.allowMSAccountLogin
        })
        this.submitting = false
        if (!res.failed) {
          this.$root.$message.showSuccess(this.$t('Saved'))
          this.$router.push({ name: this.redirectTo })
        }
      }
    },

    async loadCorporations () {
      const res = await Admin.loadCorporations()
      if (!res.failed) {
        this.corporations = res.data.map(item => {
          return {
            text: item.Name,
            value: item.Id
          }
        })
      }
    },

    async loadFacilities () {
      if (this.form.corporation !== 0) {
        const res = await Admin.loadFacilities(this.form.corporation)
        if (!res.failed) {
          this.facilities = res.data.map(item => {
            return {
              text: item.No,
              value: item.Id
            }
          })
        }
      }
    },

    async loadUserGroups () {
      if (this.form.corporation !== 0) {
        const res = await Admin.loadUserGroups(this.form.corporation)
        if (!res.failed) {
          this.usergroups = res.data.filter(g => g.Active === true).map(item => {
            return {
              text: item.Name,
              value: item.Id
            }
          })
        }
      }
    }
  },

  async mounted () {
    // We use next tick to ensure that app is initialized
    this.$nextTick(() => {
      app.$on('force-update', () => {
        // We need this to ensure that autoLogin re-renders component
        this.$forceUpdate()
      })
    })

    this.hasClassifierClaim = Security.user.token.Claims.includes('69171030') // license.js: classifierNo = '69171030'
    this.hasScanMobileClaim = Security.user.token.Claims.includes('69171055') // license.js: scanMobileNo = '69171055'
    this.hasDeliveryClaim = Security.user.token.Claims.includes('69171070') // license.js: worlPlacesNo = '69171070'
    this.hasWheelLoaderClaim = Security.user.token.Claims.includes('69171010') // license.js: wheelLoaderNo = '69171010'

    if (this.$route.params.id) {
      this.id = this.$route.params.id
      const res = await Admin.loadUser(this.id)
      if (!res.failed) {
        this.form.name = res.data.Name
        this.form.fullName = res.data.FullName
        this.form.email = res.data.Email
        this.form.corporation = res.data.CorporationId
        this.form.facilities = res.data.Facilities
        this.form.usergroups = res.data.UserGroups
        this.form.allowMSAccountLogin = res.data.AllowMSAccountLogin
        this.form.active = res.data.DeletedDate == null
        this.details = res.data.Details
        const custNoDetail = this.details.find(o => o.Name === 'CustomerNo')
        const carrierNoDetail = this.details.find(o => o.Name === 'CarrierNo')
        const producerNoDetail = this.details.find(o => o.Name === 'ProducerNo')
        const vehicleLicensePlateNoDetail = this.details.find(o => o.Name === 'VehicleLicensePlateNo')
        const disableApprovalRightsDetail = this.details.find(o => o.Name === 'DisableApprovalRights')
        const showGotoDeliveryInspectionDetail = this.details.find(o => o.Name === 'ShowGotoDeliveryInspection')
        const internalUserVehicleDetail = this.details.find(o => o.Name === 'InternalUserVehicle')
        const zplPrinterIpDetail = this.details.find(o => o.Name === 'ZplPrinterIp')
        const zplPrinterWidthDetail = this.details.find(o => o.Name === 'ZplPrinterWidth')
        const createDeliveriesAllAgreementsDetail = this.details.find(o => o.Name === 'CreateDeliveriesAllAgreements')
        const createVehiclesAllCarriersDetail = this.details.find(o => o.Name === 'CreateVehiclesAllCarriers')
        const zplPrinterIpDetailWl = this.details.find(o => o.Name === 'ZplPrinterIpWl')
        const zplPrinterWidthDetailWl = this.details.find(o => o.Name === 'ZplPrinterWidthWl')
        this.form.customerNo = custNoDetail?.Value
        this.form.carrierNo = carrierNoDetail?.Value
        this.form.producerNo = producerNoDetail?.Value
        this.form.vehicleLicensePlateNo = vehicleLicensePlateNoDetail?.Value
        this.form.disableApprovalRights = disableApprovalRightsDetail?.Value === 'true'
        this.form.showGotoDeliveryInspection = showGotoDeliveryInspectionDetail?.Value === 'true'
        this.form.internalUserVehicle = internalUserVehicleDetail?.Value === 'true'
        this.form.zplPrinterIp = zplPrinterIpDetail?.Value
        this.form.zplPrinterWidth = zplPrinterWidthDetail?.Value
        this.form.createDeliveriesAllAgreements = createDeliveriesAllAgreementsDetail?.Value === 'true'
        this.form.createVehiclesAllCarriers = createVehiclesAllCarriersDetail?.Value === 'true'
        this.form.zplPrinterIpWl = zplPrinterIpDetailWl?.Value
        this.form.zplPrinterWidthWl = zplPrinterWidthDetailWl?.Value
      }
    }

    await this.loadCorporations()
    await this.loadFacilities()
    await this.loadUserGroups()

    this.$nextTick(() => {
      this.loading = false
    })
  }
}
</script>
<style>
.v-ip {
    color: blue !important;
}
</style>
